import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { formatCurrency } from '../lib/utils';
import axios from 'axios';
import { Plus, Search, Edit2, Trash2, Package } from 'lucide-react';
import ExportButton from '../components/ExportButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ['Cakes', 'Oriental', 'French', 'Pastry', 'Chocolate', 'Cookies', 'Other'];
const groups = ['Sweets', 'Bakery', 'Uncategorized'];
const units = ['kg', 'piece', 'box', 'dozen', 'g'];

export default function ProductsPage() {
  const { t, language } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    category: 'Other',
    group: 'Uncategorized',
    unit: 'piece',
    price: '',
    cost: '',
    description: '',
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        cost: parseFloat(formData.cost),
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, data);
      } else {
        await axios.post(`${API}/products`, data);
      }
      fetchProducts();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('confirm'))) {
      try {
        await axios.delete(`${API}/products/${id}`);
        fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      name_ar: product.name_ar || '',
      category: product.category,
      group: product.group || 'Uncategorized',
      unit: product.unit,
      price: product.price.toString(),
      cost: product.cost.toString(),
      description: product.description || '',
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProduct(null);
    setFormData({
      name: '',
      name_ar: '',
      category: 'Other',
      group: 'Uncategorized',
      unit: 'piece',
      price: '',
      cost: '',
      description: '',
    });
  };

  const filteredProducts = products.filter(
    (p) =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.name_ar && p.name_ar.includes(search)) ||
      p.category.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl" data-testid="products-title">{t('products')}</h1>
          <p className="text-muted-foreground">
            {language === 'ar' ? 'إدارة منتجات الحلويات' : 'Manage your sweets products'}
          </p>
        </div>
        <div className="flex gap-2">
          <ExportButton
            endpoint="products"
            filename="products"
            label={language === 'ar' ? 'تصدير Excel' : 'Export Excel'}
          />
          <Button onClick={() => setDialogOpen(true)} data-testid="add-product-btn">
            <Plus size={18} className="me-2" />
            {t('addProduct')}
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="border-border/40">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              placeholder={t('search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="ps-10"
              data-testid="products-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card className="border-border/40">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Package size={48} className="mx-auto mb-2 opacity-50" />
              <p>{t('noData')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('productName')}</TableHead>
                    <TableHead>{t('category')}</TableHead>
                    <TableHead>{language === 'ar' ? 'القسم' : 'Group'}</TableHead>
                    <TableHead>{t('price')}</TableHead>
                    <TableHead>{t('cost')}</TableHead>
                    <TableHead className="text-end">{language === 'ar' ? 'الإجراءات' : 'Actions'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts.map((product) => (
                    <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{language === 'ar' && product.name_ar ? product.name_ar : product.name}</p>
                          {language === 'ar' && product.name_ar && (
                            <p className="text-xs text-muted-foreground">{product.name}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="px-2 py-1 rounded-full bg-secondary text-secondary-foreground text-xs">
                          {product.category}
                        </span>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const g = product.group || 'Uncategorized';
                          const styles = g === 'Sweets'
                            ? 'bg-pink-100 text-pink-800'
                            : g === 'Bakery'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-gray-100 text-gray-600';
                          const label = language === 'ar'
                            ? (g === 'Sweets' ? 'حلويات' : g === 'Bakery' ? 'مخبوزات' : 'غير مصنّف')
                            : g;
                          return (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles}`} data-testid={`product-group-${product.id}`}>
                              {label}
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="font-medium">{formatCurrency(product.price)}</TableCell>
                      <TableCell className="text-muted-foreground">{formatCurrency(product.cost)}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(product)}
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Edit2 size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(product.id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingProduct ? t('edit') : t('addProduct')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('productName')} (English)</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="product-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('productName')} (عربي)</Label>
                <Input
                  value={formData.name_ar}
                  onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                  data-testid="product-name-ar-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('category')}</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger data-testid="product-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{language === 'ar' ? 'القسم (للتقرير الأسبوعي)' : 'Group (for weekly report)'}</Label>
                <Select
                  value={formData.group}
                  onValueChange={(value) => setFormData({ ...formData, group: value })}
                >
                  <SelectTrigger data-testid="product-group-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {groups.map((g) => (
                      <SelectItem key={g} value={g}>
                        {language === 'ar'
                          ? (g === 'Sweets' ? 'حلويات' : g === 'Bakery' ? 'مخبوزات' : 'غير مصنّف')
                          : g}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('unit')}</Label>
                <Select
                  value={formData.unit}
                  onValueChange={(value) => setFormData({ ...formData, unit: value })}
                >
                  <SelectTrigger data-testid="product-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map((unit) => (
                      <SelectItem key={unit} value={unit}>
                        {unit}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('price')} (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  data-testid="product-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('cost')} (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.cost}
                  onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                  required
                  data-testid="product-cost-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                {t('cancel')}
              </Button>
              <Button type="submit" data-testid="product-submit-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
